import type { AgentPanelAdapter } from '../../model/types';
import { useNavigate } from '@tanstack/react-router';
import { Check } from 'lucide-react';
import { useCallback } from 'react';
import { pickCss } from '@/shared/lib/css-utils';
import { Button, Text } from '@/shared/ui';
import { useAgentPanelAdapter } from '../../model/adapter-context';
import { useAgentPanelStore } from '../../model/use-agent-panel-store';
import css from './done-step.module.scss';

const styles = pickCss(css, css.root);

const isItineraryAdapter = (
  adapter: AgentPanelAdapter | null
): adapter is AgentPanelAdapter<'itinerary'> =>
  adapter?.contextType === 'itinerary';

// Renders frame `igs07`. The generator now exits to the itinerary planner —
// the inbox thread-driven "Send" path was retired together with the
// `/inbox/$threadId/preview` page (day picks + send move to the itinerary
// workspace). `Open planner` opens the committed itinerary; `Close` dismisses
// the panel (e.g. on the sandbox surface where there is no itinerary).
export const DoneStep = () => {
  const navigate = useNavigate();
  const adapter = useAgentPanelAdapter();
  const generatorState = useAgentPanelStore((s) => s.generatorState);
  const close = useAgentPanelStore((s) => s.close);

  const itineraryId = isItineraryAdapter(adapter)
    ? adapter.context.itineraryId
    : null;

  const handleOpenPlanner = useCallback(() => {
    if (!itineraryId) return;
    navigate({ to: `/itineraries/${itineraryId}` });
  }, [itineraryId, navigate]);

  if (generatorState?.phase !== 'done') return null;

  const { total } = generatorState;
  const summary =
    total === 1
      ? 'The new item is now on the itinerary planner.'
      : `The ${total} new items are now on the itinerary planner.`;

  return (
    <div className={styles()}>
      <div aria-hidden="true" className={css.checkBadge}>
        <Check size={28} />
      </div>
      <Text size="lg" weight="semibold">
        {total} {total === 1 ? 'item' : 'items'} added
      </Text>
      <Text size="sm" variant="muted">
        {summary}
      </Text>
      <footer className={css.footer}>
        <Button onClick={close} size="default" type="button" variant="outline">
          Close
        </Button>
        <Button
          disabled={!itineraryId}
          onClick={handleOpenPlanner}
          size="default"
          type="button"
          variant="primary"
        >
          Open planner
        </Button>
      </footer>
    </div>
  );
};
